import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ClerkProvider } from '@clerk/clerk-react'
import { ClerkUserSync } from './components/ClerkUserSync'
import './index.css'
import App from './App.tsx'
import { config } from './config'
import { initAppInsights } from './services/appInsights'
import { initAnalytics } from './services/analytics'
import { rememberMarketingParams } from './services/googleAds'

// Correlation headers go only to our own API hosts; see services/appInsights.ts.
initAppInsights(config.appInsights.connectionString, Object.values(config.api));
initAnalytics(config.posthog.key, config.posthog.host);
// Capture gclid / UTM params from the landing URL before any client-side
// redirect can drop them (see withMarketingParams).
rememberMarketingParams();

// After a deploy, long-lived tabs may request lazy-loaded chunks whose hashed
// filenames no longer exist on the server. Reload once to pick up the new
// index.html (guard prevents a reload loop if the failure persists).
window.addEventListener('vite:preloadError', (event) => {
  const RELOAD_FLAG = 'chunk-reload-at';
  const lastReload = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
  if (Date.now() - lastReload < 10_000) return; // just tried — let the error surface
  sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  event.preventDefault(); // suppress the unhandled error; we're handling it via reload
  window.location.reload();
});

if (!config.clerk.publishableKey) {
  throw new Error('Missing Clerk Publishable Key')
}

// Conditionally wrap with StrictMode only in production
// This prevents duplicate API calls during development while keeping safety checks in production
const AppWithProviders = (
  <ClerkProvider publishableKey={config.clerk.publishableKey}
    signInUrl="/auth/login"
    signUpUrl="/auth/individual-signup">
    <ClerkUserSync>
      <App />
    </ClerkUserSync>
  </ClerkProvider>
)

createRoot(document.getElementById('root')!).render(
  import.meta.env.PROD ? <StrictMode>{AppWithProviders}</StrictMode> : AppWithProviders
)
